// ============================================
// CONTEXT DE AUTENTICACIÓN - FASE 14
// ============================================
// Maneja el estado global de autenticación + Sistema de Planes

import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

// Crear el contexto
const AuthContext = createContext(null);

// Hook personalizado para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

// Provider del contexto
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Verificar si hay un usuario al cargar la app
  useEffect(() => {
    checkAuth();
  }, []);

  // Verificar autenticación
  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoading(false);
        return;
      }

      // Verificar que el token sea válido obteniendo el perfil
      const userData = await authService.getProfile();
      setUser(userData);
      
    } catch (error) {
      console.error('Error al verificar autenticación:', error);
      const status = error.response?.status;
      
      // Solo limpiar sesión si es 401 (token inválido/expirado)
      // NO limpiar si es 429 (rate limit) o error de red (servidor caído)
      if (status === 401) {
        localStorage.removeItem('token');
        setUser(null);
      } else {
        // 429, 500, network error → mantener sesión, reintentar después
        console.warn(`⚠️ Error ${status || 'red'} al verificar auth — sesión mantenida`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Función de login
  const login = async (email, password) => {
    try {
      setError(null);
      const data = await authService.login(email, password);
      
      // Guardar token en localStorage
      localStorage.setItem('token', data.token);
      
      // Guardar usuario en el estado
      setUser(data.user);
      
      // ✅ S44: Redirect según rol
      const dest = data.user?.rol === 'TECNICO' ? '/remitos'
        : data.user?.rol === 'VENDEDOR' ? '/chat'
        : '/dashboard';
      navigate(dest);
      
      return { success: true };
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error al iniciar sesión';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Función de registro
  const register = async (nombre, email, password) => {
    try {
      setError(null);
      await authService.register(nombre, email, password);
      
      // Después del registro, hacer login automáticamente
      return await login(email, password);
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error al registrarse';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Función de logout
  const logout = () => {
    try {
      // Limpiar token
      localStorage.removeItem('token');
      
      // Limpiar usuario
      setUser(null);
      
      // Redirigir al login
      navigate('/login');
      
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  // ============================================
  // FASE 14: HELPERS DE PLAN
  // ============================================

  // Obtener info del plan actual
  const getPlan = () => {
    return user?.cliente?.plan || null;
  };

  // Obtener nivel del plan
  const getPlanLevel = () => {
    return user?.cliente?.plan?.nivel || 0;
  };

  // Verificar si tiene acceso a un feature
  const canAccessFeature = (featureKey) => {
    // SUPER_ADMIN tiene acceso a todo
    if (user?.rol === 'SUPER_ADMIN') {
      return true;
    }

    // ADMIN y VENDEDOR heredan acceso completo del plan del cliente
    // (el plan se gestiona a nivel cliente, no a nivel usuario)
    if (['ADMIN', 'VENDEDOR'].includes(user?.rol)) {
      const plan = getPlan();
      // Si tiene plan, confiar en el nivel; si no tiene plan cargado, permitir igual
      // (el backend valida permisos reales en cada endpoint)
      if (!plan || !plan.features) return true;
      const feature = plan.features[featureKey];
      return feature?.enabled !== false;
    }

    const plan = getPlan();
    if (!plan || !plan.features) {
      return false;
    }

    const feature = plan.features[featureKey];
    return feature?.enabled === true;
  };

  // Verificar si alcanzó el límite de un recurso
  const hasReachedLimit = (resourceType) => {
    // SUPER_ADMIN no tiene límites
    if (user?.rol === 'SUPER_ADMIN') {
      return false;
    }

    const plan = getPlan();
    const usage = user?.cliente?.usage || {};

    if (!plan || !plan.limites) {
      return false;
    }

    const limit = plan.limites[resourceType];
    const current = usage[resourceType] || 0;

    // -1 o null = ilimitado
    if (limit === -1 || limit === null) {
      return false;
    }

    return current >= limit;
  };

  // Obtener límite restante
  const getRemainingLimit = (resourceType) => {
    // SUPER_ADMIN no tiene límites
    if (user?.rol === 'SUPER_ADMIN') {
      return { unlimited: true };
    }

    const plan = getPlan();
    const usage = user?.cliente?.usage || {};

    if (!plan || !plan.limites) {
      return { limit: 0, current: 0, remaining: 0, percentage: 0 };
    }

    const limit = plan.limites[resourceType];
    const current = usage[resourceType] || 0;

    // -1 o null = ilimitado
    if (limit === -1 || limit === null) {
      return { unlimited: true, current };
    }

    const remaining = Math.max(0, limit - current);
    const percentage = limit > 0 ? Math.round((current / limit) * 100) : 0;

    return {
      limit,
      current,
      remaining,
      percentage,
      unlimited: false
    };
  };

  // Obtener porcentaje de progreso del plan (nivel)
  const getPlanProgress = () => {
    const level = getPlanLevel();
    const maxLevel = 5; // Enterprise es nivel 5
    return Math.round((level / maxLevel) * 100);
  };

  // Verificar si hay features bloqueadas
  const hasBlockedFeatures = () => {
    // SUPER_ADMIN no tiene features bloqueadas
    if (user?.rol === 'SUPER_ADMIN') {
      return false;
    }

    const plan = getPlan();
    if (!plan || !plan.features) {
      return true;
    }

    // Verificar si algún feature importante está bloqueado
    const importantFeatures = ['leads', 'campaigns', 'broadcasts', 'analytics'];
    return importantFeatures.some(feature => !canAccessFeature(feature));
  };

  // Obtener nombre del plan con color
  const getPlanInfo = () => {
    const plan = getPlan();
    if (!plan) {
      return { name: 'Sin plan', color: '#666', level: 0 };
    }

    return {
      name: plan.nombre,
      color: plan.color || '#666',
      level: plan.nivel,
      price: plan.precio
    };
  };

  // Valor del contexto
  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    
    // FASE 14: Helpers de plan
    getPlan,
    getPlanLevel,
    canAccessFeature,
    hasReachedLimit,
    getRemainingLimit,
    getPlanProgress,
    hasBlockedFeatures,
    getPlanInfo
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
